import React, { useEffect, useMemo, useRef, useState } from "react";
import SEO from "@/components/SEO";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Image as ImageIcon, Search, Star, StarOff, Pencil, Trash2,
  X, ShieldCheck, Cloud, Loader2, CheckCircle2, AlertCircle, Plus
} from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminTextarea, AdminSelect, StatusPill, EmptyState } from "@/admin/components/ui";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const ACCEPT_EXT = [".jpg", ".jpeg", ".png", ".webp"];

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };

export default function AdminPhotos() {
  const { id: eventId, sid: subfolderId } = useParams();
  const [data, setData] = useState({ event: null, subfolder: null, photos: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [editing, setEditing] = useState(null); // photo doc
  const [queue, setQueue] = useState([]);       // { tmpId, name, size, progress, status, error }
  const fileRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/events/${eventId}/subfolders/${subfolderId}/photos`);
      setData(r.data);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [eventId, subfolderId]);

  const list = useMemo(() => {
    return (data.photos || []).filter((p) =>
      (status === "all" || p.status === status) &&
      (!q || `${p.display_name} ${p.original_file_name}`.toLowerCase().includes(q.toLowerCase()))
    );
  }, [data.photos, q, status]);

  // ---------- Upload ----------
  const validate = (file) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ACCEPT.split(",").includes(file.type) && !ACCEPT_EXT.includes(ext)) {
      return `Unsupported file type${file.type ? `: ${file.type}` : ""}`;
    }
    if (file.size > 100 * 1024 * 1024) return "File exceeds 100 MB";
    return null;
  };

  const uploadOne = async (file, tmpId) => {
    const form = new FormData();
    form.append("file", file);
    try {
      await adminApi.post(
        `/admin/events/${eventId}/subfolders/${subfolderId}/photos`,
        form,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            setQueue((qs) => qs.map((x) => x.tmpId === tmpId ? { ...x, progress: pct } : x));
          },
        }
      );
      setQueue((qs) => qs.map((x) => x.tmpId === tmpId ? { ...x, progress: 100, status: "done" } : x));
    } catch (err) {
      setQueue((qs) => qs.map((x) => x.tmpId === tmpId ? { ...x, status: "error", error: formatErr(err) } : x));
    }
  };

  const onFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    const items = arr.map((f) => {
      const err = validate(f);
      return {
        tmpId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file: f,
        name: f.name,
        size: f.size,
        progress: err ? 0 : 1,
        status: err ? "error" : "uploading",
        error: err,
      };
    });
    setQueue((qs) => [...items, ...qs]);
    // Sequentially upload to avoid R2 throttling on big batches; could parallel-3 later.
    for (const it of items) {
      if (it.status === "error") continue;
      await uploadOne(it.file, it.tmpId);
    }
    await load();
  };

  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    if (e.dataTransfer?.files) onFiles(e.dataTransfer.files);
  };

  // ---------- Edit / delete ----------
  const saveEdit = async () => {
    try {
      const { display_name, status, is_featured, tags, notes } = editing;
      const payload = {
        display_name, status, is_featured, notes,
        tags: typeof tags === "string" ? tags.split(",").map((t) => t.trim()).filter(Boolean) : tags,
      };
      const r = await adminApi.put(`/admin/photos/${editing.id}`, payload);
      setData((d) => ({ ...d, photos: d.photos.map((p) => p.id === editing.id ? r.data : p) }));
      toast.success("Photo updated.");
      setEditing(null);
    } catch (err) { toast.error(formatErr(err)); }
  };

  const removePhoto = async (p) => {
    if (!window.confirm(`Delete "${p.display_name}"? This removes the original, preview and thumbnail from R2.`)) return;
    try {
      await adminApi.delete(`/admin/photos/${p.id}`);
      toast.success("Photo deleted.");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const toggleFeatured = async (p) => {
    try {
      const r = await adminApi.put(`/admin/photos/${p.id}`, { is_featured: !p.is_featured });
      setData((d) => ({ ...d, photos: d.photos.map((x) => x.id === p.id ? r.data : x) }));
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="page-admin-photos" className="space-y-6">
      <SEO title="AdminPhotos · WelkomBiz Photography" noindex />
      {/* Breadcrumbs */}
      <nav className="text-[10px] tracking-luxe uppercase text-white/45 flex flex-wrap items-center gap-2">
        <Link to="/admin" className="hover:text-white">Admin</Link>
        <span>›</span>
        <Link to="/admin/events" className="hover:text-white">Events</Link>
        <span>›</span>
        <Link to={`/admin/events/${eventId}/edit`} className="hover:text-white truncate max-w-[180px]">{data.event?.title || "Event"}</Link>
        <span>›</span>
        <Link to={`/admin/events/${eventId}/folders`} className="hover:text-white">Folders</Link>
        <span>›</span>
        <Link to={`/admin/events/${eventId}/folders`} className="hover:text-white">{data.subfolder?.name || "Subfolder"}</Link>
        <span>›</span>
        <span className="text-white">Photos</span>
      </nav>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <Link to={`/admin/events/${eventId}/folders`} className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Folders
          </Link>
          <h2 className="font-display text-2xl sm:text-3xl text-chrome mt-3">{data.subfolder?.name}</h2>
          <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">{data.event?.title} · {data.photos?.length || 0} photo{(data.photos?.length || 0) === 1 ? "" : "s"}</p>
        </div>
        <button data-testid="photos-upload-btn" onClick={() => fileRef.current?.click()} className="btn-primary !py-2.5 !px-5 !text-[11px]">
          <Upload className="w-3.5 h-3.5" /> Upload Photos
        </button>
        <input ref={fileRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="photos-file-input" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {/* Upload zone */}
      <div
        data-testid="photos-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        className={`relative cursor-pointer rounded-md p-8 sm:p-10 text-center transition-all chrome-border ${dragOver ? "bg-[#18d3ff]/5" : "glass"}`}
      >
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(255,46,147,0.18), transparent 70%)" }} />
        <div className="absolute -bottom-16 -left-16 w-40 h-40 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(24,211,255,0.16), transparent 70%)" }} />
        <div className="relative">
          <div className="w-14 h-14 rounded-full chrome-border bg-black/40 flex items-center justify-center mx-auto mb-4">
            <Cloud className="w-5 h-5 text-[#18d3ff]" />
          </div>
          <p className="font-display text-2xl text-chrome">Drag & drop photos here</p>
          <p className="text-sm text-white/55 font-body mt-2 max-w-lg mx-auto">
            Upload full-quality event photos. Originals are preserved <strong>privately</strong> in Cloudflare R2.
            Watermarked previews &amp; thumbnails are generated automatically for gallery viewing.
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[10px] tracking-luxe uppercase text-white/45">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3 h-3 text-[#ff2e93]" /> Originals private</span>
            <span>·</span>
            <span>JPG / PNG / WebP · up to 100 MB each</span>
          </div>
        </div>
      </div>

      {/* Upload queue */}
      {queue.length > 0 && (
        <Panel testId="upload-queue" title="Upload Queue" action={
          <button onClick={() => setQueue([])} className="text-[10px] tracking-luxe uppercase text-white/45 hover:text-white">Clear</button>
        }>
          <ul className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {queue.map((q) => (
              <li key={q.tmpId} className="flex items-center gap-3" data-testid={`upload-item-${q.tmpId}`}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center chrome-border bg-black/40 shrink-0">
                  {q.status === "uploading" ? <Loader2 className="w-3.5 h-3.5 text-[#18d3ff] animate-spin" />
                    : q.status === "done" ? <CheckCircle2 className="w-3.5 h-3.5 text-[#7fe6ff]" />
                    : <AlertCircle className="w-3.5 h-3.5 text-[#ff9fcf]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm text-white truncate">{q.name}</p>
                    <span className="text-[10px] tracking-luxe uppercase text-white/45 shrink-0">{q.status === "error" ? "FAILED" : `${q.progress}%`}</span>
                  </div>
                  <div className="h-1 mt-1 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{
                      width: `${q.status === "error" ? 100 : q.progress}%`,
                      background: q.status === "error" ? "linear-gradient(90deg, #ff2e93, #ff7fc1)" : "linear-gradient(90deg, #ff2e93, #18d3ff)",
                    }} />
                  </div>
                  {q.error && <p className="text-[10px] text-[#ff9fcf] mt-1">{q.error}</p>}
                </div>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1 glass chrome-border rounded-full px-4 py-2">
          <Search className="w-4 h-4 text-white/45" />
          <input data-testid="photos-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search photos…" className="flex-1 bg-transparent text-sm text-white placeholder-white/35 outline-none font-body" />
        </div>
        <div className="flex gap-2">
          {["all", "active", "hidden", "archived"].map((s) => (
            <button key={s} data-testid={`photos-filter-${s}`} onClick={() => setStatus(s)} className={`text-[10px] tracking-luxe uppercase px-3 py-1.5 rounded-full border ${status === s ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/55 hover:text-white"}`}>{s}</button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? <p className="text-sm text-white/40">Loading…</p> :
       list.length === 0 ? (
        <EmptyState
          testId="photos-empty"
          title="Your gallery is ready for its first photos."
          message="Upload full-quality originals and the platform will prepare premium watermarked previews and thumbnails for viewing."
        />
       ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
          {list.map((p) => (
            <div key={p.id} data-testid={`photo-card-${p.id}`} className="group relative glass chrome-border rounded-md overflow-hidden">
              <div className="relative aspect-square bg-black/60">
                {p.thumbnail_url ? (
                  <img src={p.thumbnail_url} alt={p.display_name} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/30">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                )}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity bg-gradient-to-t from-black/85 via-black/0 to-black/0 flex items-end p-3 gap-2">
                  <button onClick={() => setEditing({ ...p, tags: (p.tags || []).join(", ") })} data-testid={`photo-edit-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 text-[10px] tracking-luxe uppercase py-1.5 rounded-md border border-white/20 text-white bg-black/40 backdrop-blur-sm hover:bg-black/60">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => toggleFeatured(p)} data-testid={`photo-feature-${p.id}`} className="p-1.5 rounded-md border border-white/20 text-white bg-black/40 backdrop-blur-sm hover:bg-black/60" title={p.is_featured ? "Unfeature" : "Feature"}>
                    {p.is_featured ? <Star className="w-3.5 h-3.5 text-[#ffd166] fill-[#ffd166]" /> : <StarOff className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {p.is_featured && (
                  <div className="absolute top-2 left-2"><Star className="w-3.5 h-3.5 text-[#ffd166] fill-[#ffd166]" /></div>
                )}
                <div className="absolute top-2 right-2"><StatusPill status={p.status} /></div>
              </div>
              <div className="p-3">
                <p className="text-xs text-white truncate" title={p.display_name}>{p.display_name}</p>
                <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-1">
                  {p.original_width}×{p.original_height} · {fmtBytes(p.file_size)}
                </p>
                <div className="mt-2 flex items-center gap-2 text-[10px] text-white/45">
                  <span className={p.preview_ready ? "text-[#7fe6ff]" : "text-white/30"}>{p.preview_ready ? "Preview ✓" : "Preview pending"}</span>
                  <span>·</span>
                  <span className={p.thumbnail_ready ? "text-[#7fe6ff]" : "text-white/30"}>{p.thumbnail_ready ? "Thumb ✓" : "Thumb pending"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
       )}

      {/* Edit modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} data-testid="photo-edit-modal" className="glass-strong chrome-border rounded-md p-5 sm:p-7 max-w-3xl w-full max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-4 mb-5">
              <div className="min-w-0">
                <p className="text-[10px] tracking-luxe uppercase text-white/45">Photo Detail</p>
                <h3 className="font-display text-2xl text-chrome truncate">{editing.display_name}</h3>
                <p className="text-[10px] text-white/40 mt-1 truncate">{editing.original_file_name}</p>
              </div>
              <button onClick={() => setEditing(null)} className="w-9 h-9 rounded-full chrome-border bg-black/40 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="photo-frame chrome-border aspect-[4/3] bg-black/60">
                {editing.preview_url
                  ? <img src={editing.preview_url} alt={editing.display_name} className="w-full h-full object-contain" />
                  : <div className="w-full h-full flex items-center justify-center text-white/30"><ImageIcon className="w-8 h-8" /></div>
                }
              </div>

              <div className="space-y-3">
                <AdminInput data-testid="photo-edit-display-name" label="Display Name" value={editing.display_name || ""} onChange={(e) => setEditing({ ...editing, display_name: e.target.value })} />
                <div className="grid grid-cols-2 gap-3">
                  <AdminSelect data-testid="photo-edit-status" label="Status" value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                    <option value="active" className="bg-black">Active</option>
                    <option value="hidden" className="bg-black">Hidden</option>
                    <option value="archived" className="bg-black">Archived</option>
                  </AdminSelect>
                  <label className="flex flex-col">
                    <span className="text-[10px] tracking-luxe uppercase text-white/50 mb-2">Featured</span>
                    <button data-testid="photo-edit-featured" onClick={() => setEditing({ ...editing, is_featured: !editing.is_featured })} className={`h-[42px] rounded-md border text-[11px] tracking-luxe uppercase ${editing.is_featured ? "border-transparent bg-gradient-to-r from-[#ff2e93] to-[#18d3ff] text-white" : "border-white/15 text-white/65"}`}>
                      {editing.is_featured ? "Featured" : "Not featured"}
                    </button>
                  </label>
                </div>
                <AdminInput data-testid="photo-edit-tags" label="Tags (comma-separated)" value={editing.tags || ""} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} />
                <AdminTextarea data-testid="photo-edit-notes" label="Notes" rows={3} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />

                <div className="glass rounded-md p-3 text-[11px] text-white/55 font-body space-y-1">
                  <p><strong className="text-white/75">Dimensions:</strong> {editing.original_width}×{editing.original_height}</p>
                  <p><strong className="text-white/75">Size:</strong> {fmtBytes(editing.file_size)}</p>
                  <p><strong className="text-white/75">Uploaded:</strong> {fmtDate(editing.uploaded_at)} by {editing.uploaded_by}</p>
                  <p className="text-[10px] tracking-luxe uppercase text-white/40 mt-2 flex items-center gap-2">
                    <ShieldCheck className="w-3 h-3 text-[#ff2e93]" /> Original stored privately · no public URL
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 mt-6 sm:items-center sm:justify-between">
              <div className="flex gap-2">
                <button data-testid="photo-save" onClick={saveEdit} className="btn-primary !py-2.5 !px-5 !text-[11px]"><Plus className="w-3.5 h-3.5" /> Save</button>
                <button onClick={() => setEditing(null)} className="btn-ghost !py-2.5 !px-5 !text-[11px]"><X className="w-3.5 h-3.5" /> Cancel</button>
              </div>
              <button data-testid="photo-delete" onClick={() => removePhoto(editing)} className="text-[10px] tracking-luxe uppercase text-white/40 hover:text-[#ff7fc1] inline-flex items-center gap-2"><Trash2 className="w-3.5 h-3.5" /> Delete Photo</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
